import { LimitKeys, Timestamp, User } from '@griply/types';
import { isAfter, isSameDay } from 'date-fns';

import { unlimitedLimit } from './get-fallback-limits';

export type LimitCheckFunctionParams = {
  [LimitKeys.GoalUserPhoto]: { user: User; today: Timestamp };
  [LimitKeys.CreateGoal]: {
    totalUncompleted: number;
    user: User;
    today: Timestamp;
  };
  [LimitKeys.SubGoals]: {
    totalUncompleted: number;
    user: User;
    today: Timestamp;
  };
  [LimitKeys.CreateProject]: {
    totalUncompleted: number;
    user: User;
    today: Timestamp;
  };
  [LimitKeys.CreateHabit]: { total: number; user: User; today: Timestamp };
  [LimitKeys.HabitIcons]: { user: User; today: Timestamp };
  [LimitKeys.HabitScheduleTarget]: { user: User; today: Timestamp };
  [LimitKeys.CreateSmartList]: { total: number; user: User; today: Timestamp };
  [LimitKeys.BiometricAppLock]: { user: User; today: Timestamp };
  [LimitKeys.CustomLifeAreas]: {
    totalCustom: number;
    user: User;
    today: Timestamp;
  };
  [LimitKeys.CustomizeDefaultLifeAreas]: { user: User; today: Timestamp };
  [LimitKeys.EditLifeAreas]: { user: User; today: Timestamp };
  [LimitKeys.VisionUserPhoto]: { user: User; today: Timestamp };
  [LimitKeys.Insights]: { user: User; today: Timestamp };
  [LimitKeys.DesktopCalendarDay]: { user: User; today: Timestamp };
  [LimitKeys.DesktopCalendarWeek]: { user: User; today: Timestamp };
  [LimitKeys.DesktopCalendarFourDays]: { user: User; today: Timestamp };
  [LimitKeys.DesktopCalendarMonth]: { user: User; today: Timestamp };
  [LimitKeys.DesktopGoalsOverviewTimeline]: { user: User; today: Timestamp };
  [LimitKeys.MobileGoalsOverviewTimeline]: { user: User; today: Timestamp };
  [LimitKeys.MobileCalendar1DayView]: { user: User; today: Timestamp };
  [LimitKeys.MobileCalendar2DayView]: { user: User; today: Timestamp };
  [LimitKeys.MobileCalendar3DayView]: { user: User; today: Timestamp };
  [LimitKeys.MobileCalendarWeekView]: { user: User; today: Timestamp };
  [LimitKeys.TwoWayCalendarSync]: { user: User; today: Timestamp };
  [LimitKeys.CreateCalendarEvent]: { user: User; today: Timestamp };
  [LimitKeys.EditCalendarEvent]: { user: User; today: Timestamp };
};

const isEnforcedOnOrBefore = (
  enforcedAt: Timestamp | null | undefined,
  today: Timestamp,
): boolean => {
  if (!enforcedAt) {
    return false;
  }
  return !isAfter(enforcedAt, today) || isSameDay(enforcedAt, today);
};

const isUnlimitedOrUnenforced = (
  user: User,
  today: Timestamp,
  limitKey: LimitKeys,
): boolean =>
  user.limits[limitKey].limit === unlimitedLimit ||
  !isEnforcedOnOrBefore(user.limits[limitKey].enforcedAt, today);

const allowWhenUnenforced =
  <LimitKey extends LimitKeys>(limitKey: LimitKey) =>
  ({ user, today }: LimitCheckFunctionParams[LimitKey]): boolean =>
    isUnlimitedOrUnenforced(user, today, limitKey);

// A count limit is a ceiling, so staying under it is allowed even once enforcement has started.
const allowUnderCount =
  <LimitKey extends LimitKeys>(
    limitKey: LimitKey,
    getCount: (params: LimitCheckFunctionParams[LimitKey]) => number,
  ) =>
  (params: LimitCheckFunctionParams[LimitKey]): boolean =>
    isUnlimitedOrUnenforced(params.user, params.today, limitKey) ||
    getCount(params) < params.user.limits[limitKey].limit;

export const limitKeyFunctionMap = {
  [LimitKeys.GoalUserPhoto]: allowWhenUnenforced(LimitKeys.GoalUserPhoto),
  [LimitKeys.CreateGoal]: allowUnderCount(
    LimitKeys.CreateGoal,
    ({ totalUncompleted }) => totalUncompleted,
  ),
  [LimitKeys.SubGoals]: allowUnderCount(
    LimitKeys.SubGoals,
    ({ totalUncompleted }) => totalUncompleted,
  ),
  [LimitKeys.CreateProject]: allowUnderCount(
    LimitKeys.CreateProject,
    ({ totalUncompleted }) => totalUncompleted,
  ),
  [LimitKeys.CreateHabit]: allowUnderCount(
    LimitKeys.CreateHabit,
    ({ total }) => total,
  ),
  [LimitKeys.HabitIcons]: allowWhenUnenforced(LimitKeys.HabitIcons),
  [LimitKeys.HabitScheduleTarget]: allowWhenUnenforced(
    LimitKeys.HabitScheduleTarget,
  ),
  [LimitKeys.CreateSmartList]: allowUnderCount(
    LimitKeys.CreateSmartList,
    ({ total }) => total,
  ),
  [LimitKeys.BiometricAppLock]: allowWhenUnenforced(LimitKeys.BiometricAppLock),
  [LimitKeys.CustomLifeAreas]: allowUnderCount(
    LimitKeys.CustomLifeAreas,
    ({ totalCustom }) => totalCustom,
  ),
  [LimitKeys.CustomizeDefaultLifeAreas]: allowWhenUnenforced(
    LimitKeys.CustomizeDefaultLifeAreas,
  ),
  [LimitKeys.EditLifeAreas]: allowWhenUnenforced(LimitKeys.EditLifeAreas),
  [LimitKeys.VisionUserPhoto]: allowWhenUnenforced(LimitKeys.VisionUserPhoto),
  [LimitKeys.Insights]: allowWhenUnenforced(LimitKeys.Insights),
  [LimitKeys.DesktopCalendarDay]: allowWhenUnenforced(
    LimitKeys.DesktopCalendarDay,
  ),
  [LimitKeys.DesktopCalendarWeek]: allowWhenUnenforced(
    LimitKeys.DesktopCalendarWeek,
  ),
  [LimitKeys.DesktopCalendarFourDays]: allowWhenUnenforced(
    LimitKeys.DesktopCalendarFourDays,
  ),
  [LimitKeys.DesktopCalendarMonth]: allowWhenUnenforced(
    LimitKeys.DesktopCalendarMonth,
  ),
  [LimitKeys.DesktopGoalsOverviewTimeline]: allowWhenUnenforced(
    LimitKeys.DesktopGoalsOverviewTimeline,
  ),
  [LimitKeys.MobileGoalsOverviewTimeline]: allowWhenUnenforced(
    LimitKeys.MobileGoalsOverviewTimeline,
  ),
  [LimitKeys.MobileCalendar1DayView]: allowWhenUnenforced(
    LimitKeys.MobileCalendar1DayView,
  ),
  [LimitKeys.MobileCalendar2DayView]: allowWhenUnenforced(
    LimitKeys.MobileCalendar2DayView,
  ),
  [LimitKeys.MobileCalendar3DayView]: allowWhenUnenforced(
    LimitKeys.MobileCalendar3DayView,
  ),
  [LimitKeys.MobileCalendarWeekView]: allowWhenUnenforced(
    LimitKeys.MobileCalendarWeekView,
  ),
  [LimitKeys.TwoWayCalendarSync]: allowWhenUnenforced(
    LimitKeys.TwoWayCalendarSync,
  ),
  [LimitKeys.CreateCalendarEvent]: allowWhenUnenforced(
    LimitKeys.CreateCalendarEvent,
  ),
  [LimitKeys.EditCalendarEvent]: allowWhenUnenforced(
    LimitKeys.EditCalendarEvent,
  ),
};

export const isPremiumFeatureAllowed = <LimitKey extends LimitKeys>(
  limitKey: LimitKey,
  params: LimitCheckFunctionParams[LimitKey],
): boolean => {
  const checkFunction = limitKeyFunctionMap[limitKey] as (
    options: LimitCheckFunctionParams[LimitKey],
  ) => boolean;
  return checkFunction(params);
};
